import _ from 'underscore'

/**
 * Class with internal state that helps for register state change listeners
 * Extracted from View.js
 */
export default class StateComponent {
    constructor(initialState = {}) {
        this.state = initialState
        this._stateChangeHandlers = []

        this._freezedState = {} // variable for store state at the time when stop handling changes
        this._handleChanges = true
    }

    /**
     * Changes current state object, merging current state with specified state changes object.
     * As a side-effect, state change handlers are called to apply state changes.
     * @param {object} stateChanges
     */
    setState(stateChanges) {
        const prevState = this.state
        this.state = {...this.state, ...stateChanges}
        if (this._handleChanges) {
            this._callStateChangeHandlers(prevState)
        }
        return this
    }

    /**
     * Changes current state object, merging current state with specified state changes object.
     * Does not call state change handlers and should only be used internally
     * to keep state up-to-date with inner visual components.
     * @param stateChanges
     */
    setStateInternal(stateChanges) {
        this.state = {...this.state, ...stateChanges}
    }

    /**
     * Gets current state object.
     * @return {object}
     */
    getState() {
        return this.state
    }

    /**
     * !!! NOTE this handler called every setState and it is not good. Use onStateDeeplyChanged instead
     * Register handler, which will be called when entire state is changed.
     * @param handler
     */
    onStateChanged(handler) {
        this._stateChangeHandlers.push((state, prevState) => { if (state !== prevState) handler(state, prevState) })
    }

    /**
     * Register handler, which will be called when entire state is changed (compared for deep equality).
     * @param handler
     */
    onStateDeeplyChanged(handler) {
        this._stateChangeHandlers.push((state, prevState) => { if (!_.isEqual(state, prevState)) handler(state, prevState) })
    }

    /**
     * Register handler, which will be called when single state property is changed.
     * @param name
     * @param handler
     */
    onStatePropertyChanged(name, handler) {
        this._stateChangeHandlers.push((state, prevState) => { if (!prevState || state[name] !== prevState[name]) handler(state[name], prevState[name]) })
    }

    /**
     * Register handler, which will be called when single state property is changed (compared for deep equality).
     * @param name
     * @param handler
     */
    onStatePropertyDeeplyChanged(name, handler) {
        this._stateChangeHandlers.push((state, prevState) => {
            if (!prevState || !_.isEqual(state[name], prevState[name])) handler(state[name], prevState[name])
        })
    }

    /**
     * Register handler, which will be called when any of specified state properties is changed.
     * @param {Array} names
     * @param handler
     */
    onAnyStatePropertyChanged(names, handler) {
        this._stateChangeHandlers.push((state, prevState) => {
            if (!prevState || names.some(name => state[name] !== prevState[name])) handler(state, prevState)
        })
    }

    /**
     * Register handler, which will be called when any of specified state properties is changed (compared for deep equality).
     * @param {Array} names
     * @param handler
     */
    onAnyStatePropertyDeeplyChanged(names, handler) {
        this._stateChangeHandlers.push((state, prevState) => {
            if (!prevState || names.some(name => !_.isEqual(state[name], prevState[name]))) handler(state, prevState)
        })
    }

    /**
     * Register handler, which will be called when single state property is changed to 'true' value
     * @param name
     * @param handler
     */
    onStatePropertyBecameTrue(name, handler) {
        this._stateChangeHandlers.push((state, prevState) => {
            if ((!prevState || state[name] !== prevState[name]) && state[name] === true)
                handler(state[name], prevState[name])
        })
    }

    /**
     * Register handler, which will be called when single state property changed to equivalent of specified value
     * @param name
     * @param value
     * @param handler
     */
    onStatePropertyBecameValue(name, value, handler) {
        this._stateChangeHandlers.push((state, prevState) => {
            if ((!prevState || !_.isEqual(state[name], prevState[name])) && _.isEqual(state[name], value))
                handler(state[name], prevState[name])
        })
    }

    /**
     * Call all handlers with current state and pass prev state = empty object
     * It's means that all handlers will be handled with current state
     */
    callStateChangeHandlersWithCurrentState() {
        this._callStateChangeHandlers({})
    }

    /**
     * Stop handling state changes via #setState
     */
    stopHandlingStateChanges() {
        if (this._handleChanges === true) {
            this._handleChanges = false
            this._freezedState = this.state
        }
    }

    /**
     * Resume handling changes
     */
    resumeHandlingStateChanges(handlePendingChanges = true) {
        if (this._handleChanges === false) {
            this._handleChanges = true
            if (handlePendingChanges) {
                this._callStateChangeHandlers(this._freezedState)
            }
        }
    }

    clearStateListeners() {
        this._stateChangeHandlers = []
    }

    /**
     * Calls every state change handler, assuming specified parameter as previous state.
     * @param prevState
     * @private
     */
    _callStateChangeHandlers(prevState) {
        this._stateChangeHandlers.forEach(h => h(this.state, prevState))
    }
}



// WEBPACK FOOTER //
// ./web/js/StateComponent.js